import React, { useState } from 'react';
import { Card, LoadingSpinner } from 'components/common';
import PropTypes from 'prop-types';
import { FiBarChart2 } from 'react-icons/fi';

const GROUP_META = {
  Discovery:   { color: '#8B5CF6', bg: '#F5F3FF', label: 'Discovery' },
  Quotation:   { color: '#F59E0B', bg: '#FFFBEB', label: 'Quotation' },
  Negotiation: { color: '#3B82F6', bg: '#EFF6FF', label: 'Negotiation' },
  Job:         { color: '#10B981', bg: '#ECFDF5', label: 'Contract' },
  Execution:   { color: '#10B981', bg: '#ECFDF5', label: 'Execution' },
  Contract:    { color: '#10B981', bg: '#ECFDF5', label: 'Contract' },
  Lost:        { color: '#EF4444', bg: '#FEF2F2', label: 'Lost' },
};

const DEFAULT_COLOR = '#6366F1';

const fmt  = (n) => Number(n || 0).toLocaleString();
const fmtW = (n) => `${Number(n || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })} MT`;
const fmtM = (n) => {
  const v = Number(n || 0);
  if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000)     return `$${(v / 1_000).toFixed(0)}K`;
  return `$${v.toFixed(0)}`;
};

const PipelineStatusChart = ({ data, loading, error, onDrillDown }) => {
  const [tooltip, setTooltip] = useState({ visible: false, x: 0, y: 0, item: null, color: '' });

  if (loading) return (
    <Card className="tw-p-6 tw-border tw-border-gray-200">
      <div className="tw-flex tw-justify-center tw-py-12"><LoadingSpinner size="lg" /></div>
    </Card>
  );
  if (error) return (
    <Card className="tw-p-4 tw-bg-red-50 tw-border tw-border-red-200">
      <p className="tw-text-red-600 tw-text-sm">Error loading pipeline data</p>
    </Card>
  );
  if (!data || data.length === 0) return (
    <Card className="tw-p-6 tw-border tw-border-gray-200">
      <p className="tw-text-gray-400 tw-text-center tw-py-8">No data available</p>
    </Card>
  );

  // Group rows by status_group, preserving backend order
  const grouped = [];
  const seen    = {};
  data.forEach(item => {
    const g = item.status_group || 'Other';
    if (!seen[g]) { seen[g] = true; grouped.push({ group: g, items: [] }); }
    grouped[grouped.length - 1].items.push(item);
  });

  const total       = data.reduce((s, d) => s + d.project_count, 0);
  const totalWeight = data.reduce((s, d) => s + (d.total_weight || 0), 0);
  const maxCount    = Math.max(...data.map((d) => d.project_count), 1);

  return (
    <Card className="tw-border tw-border-gray-200 tw-bg-gradient-to-br tw-from-blue-50 tw-via-white tw-to-primary-50 tw-shadow-sm">
      {tooltip.visible && (
        <div
          style={{
            position: 'fixed', left: tooltip.x + 12, top: tooltip.y - 10,
            zIndex: 9999, pointerEvents: 'none', background: 'white',
            border: `2px solid ${tooltip.color}`, borderRadius: 10,
            padding: '10px 14px', boxShadow: '0 8px 24px rgba(0,0,0,0.12)', minWidth: 180
          }}
        >
          <p className="tw-font-bold tw-text-gray-900 tw-text-sm tw-mb-1">{tooltip.item?.sales_pipeline_name}</p>
          <p className="tw-text-lg tw-font-extrabold" style={{ color: tooltip.color }}>{fmt(tooltip.item?.project_count)} projects</p>
          <p className="tw-text-xs tw-text-gray-500">{fmtM(tooltip.item?.total_value)}</p>
          <p className="tw-text-xs tw-text-cyan-600 tw-font-semibold">{fmtW(tooltip.item?.total_weight)}</p>
          <p className="tw-text-xs tw-text-gray-400">{total > 0 ? ((tooltip.item?.project_count / total) * 100).toFixed(1) : 0}% of total</p>
        </div>
      )}

      <div className="tw-p-5 tw-border-b tw-border-gray-200">
        <h3 className="tw-font-bold tw-text-gray-900 tw-flex tw-items-center tw-gap-2">
          <FiBarChart2 className="tw-text-blue-600" size={18} />
          Pipeline by Status
        </h3>
        <p className="tw-text-xs tw-text-gray-500 tw-mt-0.5">Project count and value by sales pipeline stage</p>
      </div>

      <div className="tw-p-5 tw-space-y-4">
        {grouped.map(({ group, items }) => {
          const meta = GROUP_META[group] || { color: DEFAULT_COLOR, bg: '#F9FAFB', label: group };
          return (
            <div key={group}>
              {/* Group header */}
              <div className="tw-flex tw-items-center tw-gap-2 tw-mb-2">
                <span
                  className="tw-text-[10px] tw-font-bold tw-uppercase tw-tracking-widest tw-px-2 tw-py-0.5 tw-rounded-xl"
                  style={{ backgroundColor: meta.bg, color: meta.color }}
                >
                  {meta.label}
                </span>
                <span className="tw-flex-1 tw-h-px tw-bg-gray-100" />
              </div>

              {/* Pipeline rows in this group */}
              <div className="tw-space-y-2">
                {items.map((item) => {
                  const pct   = maxCount > 0 ? (item.project_count / maxCount) * 100 : 0;
                  const color = meta.color;
                  return (
                    <div
                      key={item.sales_pipeline_id}
                      className="tw-cursor-pointer tw-rounded tw-px-3 tw-py-2 hover:tw-bg-white hover:tw-shadow-sm tw-transition-all"
                      onMouseMove={(e) => setTooltip({ visible: true, x: e.clientX, y: e.clientY, item, color })}
                      onMouseLeave={() => setTooltip(t => ({ ...t, visible: false }))}
                      onClick={() => onDrillDown && item.project_count > 0 && onDrillDown(
                        `${item.sales_pipeline_name} (${fmt(item.project_count)})`,
                        { statuses: [item.sales_pipeline_id] }
                      )}
                    >
                      <div className="tw-flex tw-items-center tw-justify-between tw-mb-1.5">
                        <div className="tw-flex tw-items-center tw-gap-2 tw-min-w-0">
                          <span className="tw-w-2 tw-h-2 tw-rounded-xl tw-flex-shrink-0" style={{ backgroundColor: color }} />
                          <span className="tw-text-xs tw-font-medium tw-text-gray-700 tw-truncate">{item.sales_pipeline_name}</span>
                        </div>
                        <div className="tw-flex tw-items-center tw-gap-2 tw-flex-shrink-0 tw-ml-2">
                          <span className="tw-text-xs tw-font-bold tw-text-gray-900">{fmt(item.project_count)}</span>
                          <span className="tw-text-xs tw-text-gray-400">{fmtM(item.total_value)}</span>
                          {item.total_weight > 0 && (
                            <span className="tw-text-xs tw-text-cyan-600 tw-font-medium">{fmtW(item.total_weight)}</span>
                          )}
                        </div>
                      </div>
                      <div className="tw-h-1.5 tw-bg-gray-100 tw-rounded-xl tw-overflow-hidden">
                        <div
                          className="tw-h-full tw-rounded-xl tw-transition-all tw-duration-500"
                          style={{ width: `${pct}%`, backgroundColor: color, opacity: 0.75 }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      <div className="tw-px-5 tw-pb-4 tw-pt-2 tw-border-t tw-border-gray-100 tw-flex tw-items-center tw-justify-between">
        <p className="tw-text-xs tw-text-gray-400">Total: <span className="tw-font-semibold tw-text-gray-600">{fmt(total)} projects</span></p>
        {totalWeight > 0 && (
          <p className="tw-text-xs tw-text-cyan-600 tw-font-semibold">{fmtW(totalWeight)}</p>
        )}
      </div>
    </Card>
  );
};

PipelineStatusChart.propTypes = {
  data:        PropTypes.array,
  loading:     PropTypes.bool,
  error:       PropTypes.any,
  onDrillDown: PropTypes.func
};
PipelineStatusChart.defaultProps = { data: [], loading: false, error: null, onDrillDown: null };

export default PipelineStatusChart;
